import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Send } from '@mui/icons-material';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';
import * as Emoji from 'quill-emoji';
import { toast, ToastContainer } from 'react-toastify';
import { usePost } from '../../../context/PostContext';
import CustomAppBar from '../../../components/shared/CustomAppBar';

Quill.register('modules/emoji', Emoji);

const EditPostPage: React.FC = () => {
  const { selectedPost, updatePostContent, updatePostContacts } = usePost();

  const [postContent, setPostContent] = useState<string>(selectedPost?.postText || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [contact, setContact] = useState({
    name: selectedPost?.contactName || '',
    type: selectedPost?.contactType || '',
    phone: selectedPost?.contactPhone || '',
  });

  const handlePostContentChange = (value: string) => {
    setPostContent(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({ ...contact, [e.target.name]: e.target.value });
  };

  const handleSaveContent = async () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = postContent;
    const cleanText = tempDiv.innerText.trim();

    if (cleanText === '') {
      toast.error('Por favor, inserta tu publicación.');
      return;
    }

    if (!selectedPost) {
      toast.error('No se ha seleccionado un post para editar.');
      return;
    }

    setLoading(true);
    try {
      await updatePostContent(selectedPost.id, postContent);
      toast.success('Contenido actualizado correctamente.');
    } catch (error) {
      toast.error('Error al actualizar el contenido.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    if (!contact.name || !contact.type || !contact.phone) {
      toast.error('Por favor, ingresa los datos de contacto.');
      return;
    }

    const phoneRegex = /^[0-9]{8,15}$/;
    if (!phoneRegex.test(contact.phone)) {
      toast.error('Por favor, digita un número de teléfono válido.');
      return;
    }

    if (!selectedPost) {
      toast.error('No se ha seleccionado un post para editar.');
      return;
    }

    setLoading2(true);
    try {
      await updatePostContacts(selectedPost.id, contact.name, contact.type, contact.phone);
      toast.success('Contactos actualizados correctamente.');
    } catch (error) {
      toast.error('Error al actualizar los contactos.');
    } finally {
      setLoading2(false);
    }
  };

  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['emoji'],
      ],
    },
    'emoji-toolbar': true,
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'emoji'];

  return (
    <>
      {/* Navbar */}
      <CustomAppBar/>
      <Container maxWidth="lg" sx={{ marginTop: '18vh', paddingBottom: '2rem' }}>
        <Box
          sx={{
            bgcolor: 'white',
            boxShadow: 3,
            borderRadius: 2,
            padding: 3,
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
            Editar Publicación
          </Typography>
  
          <Grid container spacing={4}>

            <Grid size={{xs:12, md:8}} >
              <Box
                sx={{
                  bgcolor: '#f9f9f9',
                  borderRadius: 2,
                  padding: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                  boxShadow: 2,
                }}
              >
                <ReactQuill
                  value={postContent}
                  onChange={handlePostContentChange}
                  modules={modules}
                  formats={formats}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Arial, sans-serif',
                    backgroundColor: 'transparent',
                    flexGrow: 1,
                    height: '231px',
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleSaveContent}
                  sx={{
                    mt: 7,
                    alignSelf: 'flex-start',
                    background: 'linear-gradient(135deg, #007bff, #0056b3)',
                    color: 'white',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    padding: '8px 15px',
                    textTransform: 'none',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: '0px 4px 8px rgba(0, 123, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0056b3, #003c7a)',
                      transform: 'scale(1.05)',
                      boxShadow: '0px 6px 12px rgba(0, 123, 255, 0.4)',
                    },
                  }}
                >
                  {loading ? 'Guardando contenido...' : 'Guardar Contenido'}
                </Button>
              </Box>
            </Grid>
  
            <Grid size={{xs:12, md:4}} >
              <Box
                sx={{
                  bgcolor: '#fafafa',
                  borderRadius: 2,
                  padding: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                  boxShadow: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontSize: 18, fontWeight: 'bold' }}>
                  Información de Contacto
                </Typography>
                <TextField
                  label="Nombre"
                  name="name"
                  value={contact.name}
                  onChange={handleInputChange}
                    size="medium"
                  fullWidth
                  sx={{ mb: 3 }}
                />
                <TextField
                  label="Tipo (ej: Vendedor, Comprador, etc.)"
                  name="type"
                  value={contact.type}
                  onChange={handleInputChange}
                  size="medium"
                  fullWidth
                  sx={{ mb: 3 }}
                />
                <TextField
                  label="Teléfono"
                  type="tel"
                  name="phone"
                  value={contact.phone}
                  onChange={handleInputChange}
                    size="medium"
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleSaveContact}
                  sx={{
                    mt: 1.3,
                    width: '100%',
                    background: 'linear-gradient(135deg, #007bff, #0056b3)',
                    color: 'white',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    padding: '8px 15px',
                    textTransform: 'none',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: '0px 4px 8px rgba(0, 123, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0056b3, #003c7a)',
                      transform: 'scale(1.05)',
                      boxShadow: '0px 6px 12px rgba(0, 123, 255, 0.4)',
                    },
                  }}
                >
                  {loading2 ? 'Guardando contactos...' : 'Guardar Contactos'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <ToastContainer />
      </Container>
    </>
  );
  
  
};

export default EditPostPage;